import { union } from 'lodash';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Key } from '../../../models/common';

export class CheckableRowsService {
    private _rowsCheckedMap = new Map<Key, boolean>();
    private _rowKeys: Key[] = [];
    private _currentPageRowKeys: Key[] = [];
    private _currentAllFilteredRowKeys: Key[] = [];
    private _cachedGroupsMap = new Map<any, Key[]>();
    public checkedRowsSubject = new BehaviorSubject<Key[]>([]);

    private _areAllRowsCheckedSubject = new ReplaySubject<boolean>(1);
    private _areAllFilteredRowsCheckedSubject = new ReplaySubject<boolean>(1);
    private _areAllPageRowsCheckedSubject = new ReplaySubject<boolean>(1);
    public areAllRowsChecked$ = this._areAllRowsCheckedSubject.asObservable();
    public areAllFilteredRowsChecked$ = this._areAllFilteredRowsCheckedSubject.asObservable();
    public areAllPageRowsChecked$ = this._areAllPageRowsCheckedSubject.asObservable();

    private _isLoadingSubject = new BehaviorSubject<boolean>(false);
    public isLoading$ = this._isLoadingSubject.asObservable();

    public init(rows: Key[]): void {
        this.addRows(rows);
    }

    public setLoading(isLoading: boolean): void {
        this._isLoadingSubject.next(isLoading);
    }

    public reset(): void {
        if (this._rowsCheckedMap) {
            this._rowsCheckedMap.clear();
        }
        this._rowsCheckedMap = new Map<Key, boolean>();
        this._rowKeys = [];
        this._currentPageRowKeys = [];
        this._currentAllFilteredRowKeys = [];
        this._cachedGroupsMap = new Map<any, Key[]>();
        this._emitChange();
    }

    public addRows(rows: Key[]): void {
        this._rowKeys = union(this._rowKeys, rows);
        rows.forEach((row) => {
            if (!this._rowsCheckedMap.has(row)) {
                this._rowsCheckedMap.set(row, false);
            }
        });
        this._emitChange();
    }

    public setCurrentPageKeys(rows: Key[]): void {
        this._currentPageRowKeys = rows;
        this._emitChange();
    }

    public setCurrentAllFilteredRowKeys(rows: Key[]): void {
        this._currentAllFilteredRowKeys = rows;
        this._emitChange();
    }

    public get checkedRows(): Key[] {
        return this._rowKeys.filter((row) => this._rowsCheckedMap.get(row));
    }

    public get rowCount(): number {
        return this._rowKeys.length;
    }

    public get currentPageRowCount(): number {
        return this._currentPageRowKeys.length;
    }

    public get currentAllFilteredRowCount(): number {
        return this._currentAllFilteredRowKeys.length;
    }

    public check(row: Key): void {
        this._rowsCheckedMap.set(row, true);
        this._emitChange();
    }

    public uncheck(row: Key): void {
        this._rowsCheckedMap.set(row, false);
        this._emitChange();
    }

    public toggleCheck(row: Key): void {
        if (this.isRowChecked(row)) {
            this.uncheck(row);
        } else {
            this.check(row);
        }
    }

    public checkMany(rows: Key[]): void {
        rows.forEach((row) => {
            this._rowsCheckedMap.set(row, true);
        });
        this._emitChange();
    }

    public checkAll(): void {
        this.checkMany(this._rowKeys);
    }

    public uncheckMany(rows: Key[]): void {
        rows.forEach((row) => {
            this._rowsCheckedMap.set(row, false);
        });
        this._emitChange();
    }

    public uncheckAll(): void {
        this._rowKeys.forEach((row) => {
            this._rowsCheckedMap.set(row, false);
        });
        this._emitChange();
    }

    public checkAllFiltered(): void {
        this.checkMany(this._currentAllFilteredRowKeys);
    }

    public uncheckAllFiltered(): void {
        this._currentAllFilteredRowKeys.forEach((row) => {
            this._rowsCheckedMap.set(row, false);
        });
        this._emitChange();
    }

    public checkPageAll(): void {
        this.checkMany(this._currentPageRowKeys);
    }

    public uncheckPageAll(): void {
        this.uncheckMany(this._currentPageRowKeys);
    }

    public isRowChecked(row: Key): boolean {
        return this._rowsCheckedMap.get(row);
    }

    public addGroupCache(groupLookupKey: any, rows: Key[]): void {
        this._cachedGroupsMap.set(groupLookupKey, rows);
    }

    public getCachedGroup(groupKey: any): Key[] {
        return this._cachedGroupsMap.get(groupKey);
    }

    public areAllRowsChecked(): boolean {
        return (
            this._rowKeys.length > 0 &&
            this._rowKeys.length ===
                Array.from(this._rowsCheckedMap).filter(([_, isChecked]) => isChecked).length
        );
    }

    public areAllFilteredRowsChecked(): boolean {
        return this._currentAllFilteredRowKeys.every((rowKey) => this._rowsCheckedMap.get(rowKey));
    }

    public areAllPageRowsChecked(): boolean {
        let allChecked = this._currentPageRowKeys?.length ? true : false;
        this._currentPageRowKeys.forEach((pageKey) => {
            if (!this._rowsCheckedMap.get(pageKey)) {
                allChecked = false;
                return;
            }
        });
        return allChecked;
    }

    public areAllRowsInGroupChecked(groupLookupKey: any): boolean {
        const groupRows = this._cachedGroupsMap.get(groupLookupKey);
        if (groupRows) {
            return groupRows.every((row) => !!this._rowsCheckedMap.get(row));
        }
        return false;
    }

    public isEmpty(): boolean {
        return this._rowKeys.length === 0;
    }

    private _emitChange(): void {
        this._areAllRowsCheckedSubject.next(this.areAllRowsChecked());
        this._areAllFilteredRowsCheckedSubject.next(this.areAllFilteredRowsChecked());
        this._areAllPageRowsCheckedSubject.next(this.areAllPageRowsChecked());
        this.checkedRowsSubject.next(this.checkedRows);
    }
}
